import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { ChatRestriction, useDropPrivileges } from "@/hooks/useDropPriviledges";
import { useWaveEligibility } from "@/contexts/wave/WaveEligibilityContext";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useAuth } from "../auth/Auth";
import DropPlaceholder from "./DropPlaceholder";
import CreateDrop from "./CreateDrop";
import { DropMode, type CurationComposerVariant, type IdentityPickerPlacement } from "./dropComposer.types";
export { DropMode } from "./dropComposer.types";
interface PrivilegedDropCreatorProps {
  readonly activeDrop: ActiveDropState | null;
  readonly onCancelReplyQuote: () => void;
  readonly onDropAddedToQueue: () => void;
  readonly onAllDropsAdded?: (() => void) | undefined;
  readonly onExitFixedDropMode?: (() => void) | undefined;
  readonly wave: ApiWave;
  readonly dropId: string | null;
  readonly fixedDropMode: DropMode;
  readonly curationComposerVariant?: CurationComposerVariant | undefined;
  readonly initialCurationUrl?: string | null | undefined;
  readonly onSubmitCurationUrl?: ((url: string) => void) | undefined;
  readonly canSubmitCurationUrl?: boolean | undefined;
  readonly curationUrlSubmitRestrictionMessage?: string | null | undefined;
  readonly externalAttachmentDrop?: {
    readonly token: number;
    readonly files: File[];
  } | null | undefined;
  readonly onExternalAttachmentDropConsumed?: (() => void) | undefined;
  readonly termsSignatureFlowEnabled?: boolean | undefined;
  readonly identityPickerPlacement?: IdentityPickerPlacement | undefined;
}
export default function PrivilegedDropCreator(t0) {
  const $ = _c(50);
  const {
    activeDrop,
    onCancelReplyQuote,
    wave,
    dropId,
    fixedDropMode,
    onAllDropsAdded,
    onExitFixedDropMode,
    onDropAddedToQueue,
    curationComposerVariant: t1,
    initialCurationUrl: t2,
    onSubmitCurationUrl,
    canSubmitCurationUrl,
    curationUrlSubmitRestrictionMessage,
    externalAttachmentDrop,
    onExternalAttachmentDropConsumed,
    termsSignatureFlowEnabled: t3,
    identityPickerPlacement: t4
  } = t0;
  const curationComposerVariant = t1 === undefined ? "default" : t1;
  const initialCurationUrl = t2 === undefined ? null : t2;
  const termsSignatureFlowEnabled = t3 === undefined ? true : t3;
  const identityPickerPlacement = t4 === undefined ? "modal" : t4;
  const queryClient = useQueryClient();
  const {
    connectedProfile,
    activeProfileProxy
  } = useAuth();
  const {
    updateEligibility
  } = useWaveEligibility();
  let t5;
  if ($[0] !== queryClient || $[1] !== wave.id) {
    t5 = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVE, {
          wave_id: wave.id
        }]
      }).catch(_temp);
    };
    $[0] = queryClient;
    $[1] = wave.id;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const refreshWaveAfterSlowModeExpires = t5;
  const t6 = !!connectedProfile?.handle;
  const t7 = !!activeProfileProxy;
  const t8 = !wave.chat.enabled;
  const t9 = wave.chat.slow_mode_cooldown_ms ?? null;
  const t10 = wave.chat.next_drop_allowed ?? null;
  const t11 = wave.participation.period?.min ?? null;
  const t12 = wave.participation.period?.max ?? null;
  const t13 = wave.participation.no_of_applications_allowed_per_participant ?? null;
  let t14;
  if ($[3] !== refreshWaveAfterSlowModeExpires || $[4] !== t10 || $[5] !== t11 || $[6] !== t12 || $[7] !== t13 || $[8] !== t6 || $[9] !== t7 || $[10] !== t8 || $[11] !== t9 || $[12] !== wave.chat.authenticated_user_eligible || $[13] !== wave.metrics.your_participation_drops_count || $[14] !== wave.participation.authenticated_user_eligible) {
    t14 = {
      isLoggedIn: t6,
      isProxy: t7,
      canChat: wave.chat.authenticated_user_eligible,
      canDrop: wave.participation.authenticated_user_eligible,
      chatDisabled: t8,
      slowModeCooldownMs: t9,
      nextDropAllowed: t10,
      submissionStarts: t11,
      submissionEnds: t12,
      maxDropsCount: t13,
      identityDropsCount: wave.metrics.your_participation_drops_count,
      onSlowModeCooldownExpired: refreshWaveAfterSlowModeExpires
    };
    $[3] = refreshWaveAfterSlowModeExpires;
    $[4] = t10;
    $[5] = t11;
    $[6] = t12;
    $[7] = t13;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
    $[12] = wave.chat.authenticated_user_eligible;
    $[13] = wave.metrics.your_participation_drops_count;
    $[14] = wave.participation.authenticated_user_eligible;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const {
    submissionRestriction,
    chatRestriction
  } = useDropPrivileges(t14);
  const blockingChatRestriction = chatRestriction === ChatRestriction.SLOW_MODE ? null : chatRestriction;
  let t15;
  let t16;
  if ($[16] !== chatRestriction || $[17] !== updateEligibility || $[18] !== wave.id) {
    t15 = () => {
      updateEligibility(wave.id, {
        authenticated_user_chat_restriction: chatRestriction
      });
    };
    t16 = [chatRestriction, updateEligibility, wave.id];
    $[16] = chatRestriction;
    $[17] = updateEligibility;
    $[18] = wave.id;
    $[19] = t15;
    $[20] = t16;
  } else {
    t15 = $[19];
    t16 = $[20];
  }
  useEffect(t15, t16);
  if (submissionRestriction !== null && blockingChatRestriction !== null) {
    let t17;
    if ($[21] !== blockingChatRestriction || $[22] !== submissionRestriction) {
      t17 = <DropPlaceholder type="both" chatRestriction={blockingChatRestriction} submissionRestriction={submissionRestriction} />;
      $[21] = blockingChatRestriction;
      $[22] = submissionRestriction;
      $[23] = t17;
    } else {
      t17 = $[23];
    }
    return t17;
  }
  if (fixedDropMode === DropMode.CHAT && blockingChatRestriction !== null) {
    let t17;
    if ($[24] !== blockingChatRestriction) {
      t17 = <DropPlaceholder type="chat" chatRestriction={blockingChatRestriction} />;
      $[24] = blockingChatRestriction;
      $[25] = t17;
    } else {
      t17 = $[25];
    }
    return t17;
  }
  if (fixedDropMode === DropMode.PARTICIPATION && submissionRestriction !== null) {
    let t17;
    if ($[26] !== submissionRestriction) {
      t17 = <DropPlaceholder type="submission" submissionRestriction={submissionRestriction} />;
      $[26] = submissionRestriction;
      $[27] = t17;
    } else {
      t17 = $[27];
    }
    return t17;
  }
  let t17;
  if ($[28] !== chatRestriction || $[29] !== submissionRestriction) {
    t17 = {
      submissionRestriction,
      chatRestriction
    };
    $[28] = chatRestriction;
    $[29] = submissionRestriction;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== activeDrop || $[32] !== canSubmitCurationUrl || $[33] !== curationComposerVariant || $[34] !== curationUrlSubmitRestrictionMessage || $[35] !== dropId || $[36] !== externalAttachmentDrop || $[37] !== fixedDropMode || $[38] !== identityPickerPlacement || $[39] !== initialCurationUrl || $[40] !== onAllDropsAdded || $[41] !== onCancelReplyQuote || $[42] !== onDropAddedToQueue || $[43] !== onExitFixedDropMode || $[44] !== onExternalAttachmentDropConsumed || $[45] !== onSubmitCurationUrl || $[46] !== t17 || $[47] !== termsSignatureFlowEnabled || $[48] !== wave) {
    t18 = <CreateDrop activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} onAllDropsAdded={onAllDropsAdded} onExitFixedDropMode={onExitFixedDropMode} wave={wave} dropId={dropId} fixedDropMode={fixedDropMode} privileges={t17} onDropAddedToQueue={onDropAddedToQueue} curationComposerVariant={curationComposerVariant} initialCurationUrl={initialCurationUrl} onSubmitCurationUrl={onSubmitCurationUrl} canSubmitCurationUrl={canSubmitCurationUrl} curationUrlSubmitRestrictionMessage={curationUrlSubmitRestrictionMessage} externalAttachmentDrop={externalAttachmentDrop} onExternalAttachmentDropConsumed={onExternalAttachmentDropConsumed} termsSignatureFlowEnabled={termsSignatureFlowEnabled} identityPickerPlacement={identityPickerPlacement} />;
    $[31] = activeDrop;
    $[32] = canSubmitCurationUrl;
    $[33] = curationComposerVariant;
    $[34] = curationUrlSubmitRestrictionMessage;
    $[35] = dropId;
    $[36] = externalAttachmentDrop;
    $[37] = fixedDropMode;
    $[38] = identityPickerPlacement;
    $[39] = initialCurationUrl;
    $[40] = onAllDropsAdded;
    $[41] = onCancelReplyQuote;
    $[42] = onDropAddedToQueue;
    $[43] = onExitFixedDropMode;
    $[44] = onExternalAttachmentDropConsumed;
    $[45] = onSubmitCurationUrl;
    $[46] = t17;
    $[47] = termsSignatureFlowEnabled;
    $[48] = wave;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  return t18;
}
function _temp() {}